import {
  ActivityStatus,
  QActivitySequence,
  QStack,
  QText,
} from '@qualio/ui-components';
import { Parser } from 'html-to-react';
import { useTranslation } from 'react-i18next';
import { ChangeControlComment } from '../../../../../api/changeControl';
import { useFormatDate } from '../../../../../hooks/useFormatDate';
import { HistoricalCommentThread } from './HistoricalCommentThreadList';

interface HistoricalCommentThreadProps {
  thread: HistoricalCommentThread;
}

export const parseHistoricalCommentThreadsForDisplay = (
  comments: ChangeControlComment[],
): HistoricalCommentThread[] => {
  let commentThreads: HistoricalCommentThread[] = [];
  commentThreads = comments
    .filter((comment) => {
      return !comment.reply_to;
    })
    .sort((a, b) => {
      return b.timestamp - a.timestamp;
    })
    .reduce((previousVal, currentVal) => {
      const commentThread = {
        ...currentVal,
        replies: comments.filter((comment: ChangeControlComment) => {
          return currentVal.id === comment.reply_to;
        }),
      };
      previousVal.push(commentThread);
      return previousVal;
    }, commentThreads);
  return commentThreads;
};

export const parseHistoricalCommentInThread = (
  thread: HistoricalCommentThread,
  formatDate: (utcDate: Date | string, includeTime?: boolean) => string,
  t: (key: string) => string,
) => {
  const htmlToReactParser = new Parser();
  const allComments = [thread, ...thread.replies];
  return allComments.map((comment: ChangeControlComment) => ({
    status: 'commented' as ActivityStatus,
    actionLabel: t('comments.commentedOnHistoricalThread'),
    fullName: comment.user.full_name ?? comment.user.id,
    actionTimestamp: formatDate(new Date(comment.timestamp * 1000), true),
    children: (
      <QStack>
        <QText>{htmlToReactParser.parse(comment.content)}</QText>
      </QStack>
    ),
  }));
};

export const HistoricalOpenCommentThread = ({
  thread,
}: HistoricalCommentThreadProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const activities = parseHistoricalCommentInThread(thread, formatDate, t);
  return <QActivitySequence activities={activities} key={thread.id} />;
};

export const HistoricalResolvedCommentThread = ({
  thread,
}: HistoricalCommentThreadProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const activities = [
    ...parseHistoricalCommentInThread(thread, formatDate, t),
    {
      status: 'markedResolved' as ActivityStatus,
      fullName: thread.user.full_name,
      actionTimestamp: t('comments.closedHistoricalThread'),
    },
  ];
  return <QActivitySequence activities={activities} key={thread.id} />;
};
